export type MissionType = "self" | "parent";

export interface Mission {
  id: string;
  emoji: string;
  title: string;
  desc: string;
  xp: number;
  type: MissionType;
  tag: string;
}

export const MISSIONS: Mission[] = [
  // ── Self-report ───────────────────────────────────────────────────────────
  {
    id: "m_track_3days",
    emoji: "📓", title: "Track Your Spending",
    desc: "For 3 days in a row, write down every dollar you spend — big or small.",
    xp: 75, type: "self", tag: "Tracking",
  },
  {
    id: "m_skip_buy",
    emoji: "💪", title: "Skip One Purchase",
    desc: "Resist buying something you wanted and move that money into savings instead.",
    xp: 75, type: "self", tag: "Saving",
  },
  {
    id: "m_research_price",
    emoji: "🔍", title: "Price Hunt",
    desc: "Find the same item at 3 different stores or websites. Which is cheapest?",
    xp: 50, type: "self", tag: "Spending",
  },
  {
    id: "m_habit_cost",
    emoji: "🧮", title: "Calculate a Daily Habit",
    desc: "Pick one daily habit (snacks, drinks, apps). Multiply the daily cost × 365. Surprised?",
    xp: 75, type: "self", tag: "Spending",
  },
  {
    id: "m_dream_timeline",
    emoji: "✏️", title: "Dream Purchase Timeline",
    desc: "Write down your #1 want and exactly how many weeks it would take to save for it.",
    xp: 50, type: "self", tag: "Goals",
  },
  {
    id: "m_ask_tip",
    emoji: "💬", title: "Ask for a Money Tip",
    desc: "Ask any adult — parent, teacher, family friend — for their single best money tip.",
    xp: 50, type: "self", tag: "Learning",
  },
  {
    id: "m_rule72",
    emoji: "📐", title: "Rule of 72",
    desc: "Use the Rule of 72 on the What If screen. Find out how long until your savings doubles.",
    xp: 75, type: "self", tag: "Learning",
  },
  {
    id: "m_hourly_cost",
    emoji: "⏰", title: "What Does It REALLY Cost?",
    desc: "Pick something you want. Divide the price by your hourly wage. How many hours is it worth?",
    xp: 75, type: "self", tag: "Spending",
  },
  {
    id: "m_weekly_budget",
    emoji: "📅", title: "Plan Your Week's Budget",
    desc: "Before the week starts, write down exactly how much you plan to spend — and stick to it.",
    xp: 100, type: "self", tag: "Budgeting",
  },
  {
    id: "m_store_brand",
    emoji: "🏷️", title: "Try the Store Brand",
    desc: "Buy the store/generic version of something instead of the name brand. Save the difference.",
    xp: 50, type: "self", tag: "Spending",
  },
  {
    id: "m_find_sell",
    emoji: "📦", title: "Declutter for Cash",
    desc: "Find 3 things you own that you no longer use and could sell or donate.",
    xp: 50, type: "self", tag: "Earning",
  },
  {
    id: "m_subscription_check",
    emoji: "📱", title: "Subscription Audit",
    desc: "Find one subscription your family pays for. Multiply the monthly cost × 12. Is it worth it?",
    xp: 75, type: "self", tag: "Tracking",
  },

  // ── Parent-verified ───────────────────────────────────────────────────────
  {
    id: "m_bank_statement",
    emoji: "🏦", title: "Read a Bank Statement",
    desc: "Ask a parent to show you a real bank or credit card statement. Find 3 transactions together.",
    xp: 100, type: "parent", tag: "Real Life",
  },
  {
    id: "m_receipt_tax",
    emoji: "🧾", title: "Spot the Tax",
    desc: "Find a receipt together. Identify the tax amount and calculate what percentage it is.",
    xp: 75, type: "parent", tag: "Real Life",
  },
  {
    id: "m_open_account",
    emoji: "🔓", title: "Open a Savings Account",
    desc: "Open a real savings account (or check on an existing one) with a parent.",
    xp: 150, type: "parent", tag: "Saving",
  },
  {
    id: "m_earn_extra",
    emoji: "💼", title: "Earn Extra Money",
    desc: "Do a chore or task beyond your normal duties and get paid for it.",
    xp: 100, type: "parent", tag: "Earning",
  },
  {
    id: "m_family_bill",
    emoji: "💡", title: "Learn a Monthly Bill",
    desc: "Ask a parent about one monthly bill (electricity, internet, etc.). What does it cost?",
    xp: 75, type: "parent", tag: "Real Life",
  },
  {
    id: "m_donate",
    emoji: "🤝", title: "Give Back",
    desc: "Donate something — money, old clothes, or your time — to someone who needs it.",
    xp: 100, type: "parent", tag: "Giving",
  },
  {
    id: "m_negotiate",
    emoji: "🤝", title: "Negotiate a Price",
    desc: "Try to get a discount or better deal on something — ask for a coupon or lower price.",
    xp: 125, type: "parent", tag: "Spending",
  },
  {
    id: "m_invest_talk",
    emoji: "📈", title: "Investing Conversation",
    desc: "Have a 5-minute conversation with a parent about how they invest or save for the future.",
    xp: 100, type: "parent", tag: "Learning",
  },
  {
    id: "m_coupon",
    emoji: "✂️", title: "Use a Coupon",
    desc: "Find and actually use a coupon or promo code before making a purchase.",
    xp: 75, type: "parent", tag: "Spending",
  },
  {
    id: "m_family_goal",
    emoji: "🎯", title: "Set a Family Savings Goal",
    desc: "Agree on a shared savings goal with a family member — a trip, a toy, a treat.",
    xp: 125, type: "parent", tag: "Goals",
  },
  {
    id: "m_cook_meal",
    emoji: "👨‍🍳", title: "Cook Instead of Order",
    desc: "Cook or help cook a full meal at home when you would have eaten out.",
    xp: 75, type: "parent", tag: "Real Life",
  },
  {
    id: "m_gratitude",
    emoji: "🙏", title: "Gratitude List",
    desc: "Write down 3 things you already own that truly make your life better. Share with a parent.",
    xp: 50, type: "parent", tag: "Mindset",
  },
];

export const EMPTY_MISSIONS = {
  completed: [] as string[],
  pendingVerify: [] as string[],
};

export function getThisWeeksMissions(): Mission[] {
  const weekNum = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  const start   = (weekNum * 3) % MISSIONS.length;
  return [
    MISSIONS[start % MISSIONS.length],
    MISSIONS[(start + 1) % MISSIONS.length],
    MISSIONS[(start + 2) % MISSIONS.length],
  ];
}
